import { useContext, useEffect, useState } from "react";
import IProcessed_Report from "@models/IProcessed_Report";
import IOnGetAvgQtyByAccountDTO, { IAvgQtyItem } from "@models/DTO/IOnGetAvgQtyByAccountDTO";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import UIService from "../../../../services/UIService";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import "./AvgQtyByAccountReport.scss";

type Props = {
    processedReport: IProcessed_Report;
};

export const AvgQtyByAccountReport = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const reportData = props.processedReport.report_data as IOnGetAvgQtyByAccountDTO;

    const [collapsedItems, setCollapsedItems] = useState<Set<string>>(new Set());
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank");
        }
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const toggleItem = (itemGuid: string) => {
        setCollapsedItems((prev) => {
            const next = new Set(prev);
            if (next.has(itemGuid)) {
                next.delete(itemGuid);
            } else {
                next.add(itemGuid);
            }
            return next;
        });
    };

    const formatQty = (qty: number) => {
        return qty.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const isLight = UIService.isMainViewLight();

    const items: IAvgQtyItem[] = reportData?.items || [];

    return (
        <div className="AvgQtyByAccountReport">
            {/* Header with Excel download and summary */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <OwLabel
                        className="me-3"
                        title={downloadingExcel ? "generating_excel" : "excel"}
                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                        fn={handleDownloadExcel}
                    />
                </div>
                <div className="d-flex gap-4">
                    <span>
                        <strong>Period:</strong> {reportData?.periodStart} &mdash; {reportData?.periodEnd}
                    </span>
                    <span>
                        <strong>Total Items:</strong> {reportData?.summary?.totalItems || 0}
                    </span>
                    <span>
                        <strong>Total Accounts:</strong> {reportData?.summary?.totalAccounts || 0}
                    </span>
                </div>
            </div>

            {/* Item Groups */}
            {items.length === 0 ? (
                <div className="text-muted">No data available.</div>
            ) : (
                <div>
                    {items.map((item) => {
                        const isCollapsed = collapsedItems.has(item.catalogItemGuid);

                        return (
                            <div key={item.catalogItemGuid} className={"item-group mb-3"}>
                                {/* Item Header */}
                                <div
                                    className={
                                        "item-group-header d-flex justify-content-between align-items-center p-2 " +
                                        (isLight ? "bg-light" : "secondary-bg-color")
                                    }
                                    onClick={() => toggleItem(item.catalogItemGuid)}
                                    style={{ cursor: "pointer" }}>
                                    <div className="d-flex align-items-center">
                                        <i className={"fas fa-chevron-" + (isCollapsed ? "right" : "down") + " me-2"} />
                                        <strong>{item.catalogItemName}</strong>
                                        <span className="text-muted ms-2">({item.sku})</span>
                                        <span className="badge bg-secondary ms-2">{item.accounts.length} accounts</span>
                                    </div>
                                    <span>
                                        <strong>Total Qty:</strong> {formatQty(item.totalQtyAllAccounts)}
                                    </span>
                                </div>

                                {/* Accounts Table */}
                                {!isCollapsed && (
                                    <table className={"table table-sm table-striped mb-0 " + (isLight ? "" : "table-dark")}>
                                        <thead>
                                            <tr>
                                                <th>Account Name</th>
                                                <th className="text-end">Total Qty</th>
                                                <th className="text-end">Months Active</th>
                                                <th className="text-end">Avg Monthly Qty</th>
                                                <th className="text-end">Avg Weekly Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {item.accounts.map((account) => (
                                                <tr key={account.vendorGuid}>
                                                    <td>{account.vendorName}</td>
                                                    <td className="text-end">{formatQty(account.totalQty)}</td>
                                                    <td className="text-end">{account.monthsActive}</td>
                                                    <td className="text-end">{formatQty(account.avgMonthlyQty)}</td>
                                                    <td className="text-end">{formatQty(account.avgWeeklyQty)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
